import { Component, DestroyRef, HostBinding, Input, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable } from 'rxjs';
import { CommonModule } from '@angular/common';
import { SearchConfig, API_STATUS } from '../../model/api.model';
import { exportConfig } from '../../model/dynamic-data-table.model';
import { InfoFieldConfig, INFO_TYPES } from '../../model/form.model';
import { CommonService } from '../../services/common.service';
import { CommonUtils } from '../../utils/common.utils';
import { ExcelExportService } from '../../services/excel-export.service';
import { DownloadButtonComponent } from '../download-button/download-button.component';
import { LoadingSkeletonComponent } from '../../layout/loading-skeleton/loading-skeleton.component';

export type ProgressColorClass = 'primary' | 'accent' | 'muted';
export type ProgressLayout = 'column' | 'row';

export interface ProgressBarItem {
  label: string;
  count?: number;
  countDisplay?: string;
  pct: number;
  colorClass?: ProgressColorClass;
  animDelay?: number;
  caption?: string;
  metaCaption?: string;
  rightCaption?: string;
  layout?: ProgressLayout;
  responseDetails?: Array<Record<string, any>>;
}

export type ProgressViewMode = 'grid' | 'list';

@Component({
  selector: 'app-dynamic-progress-bar',
  standalone: true,
  imports: [CommonModule, DownloadButtonComponent, LoadingSkeletonComponent],
  templateUrl: './dynamic-progress-bar.component.html',
  styleUrls: ['./dynamic-progress-bar.component.scss']
})
export class DynamicProgressBarComponent implements OnInit {
  @Input() elementId = '';
  @Input() title = '';
  @Input() summary = '';
  @Input() bars: ProgressBarItem[] = [];
  @Input() apiConfig?: SearchConfig;
  @Input() dataTransform?: (response: any) => ProgressBarItem[];
  @Input() infoFields?: InfoFieldConfig[];
  @Input() exportConfig?: exportConfig;
  @Input() detailsExportConfig?: exportConfig;
  @Input() desktopColumns = 5;
  @Input() viewMode: ProgressViewMode = 'grid';
  @Input() globalPayloadSubject?: Observable<any>;
  @Input() globalFilterFields: string[] = [];
  @Input() containerClass?: string;
  @Input() centerContent = false;
  @Input() onLoadFetch = true;
  isLoading = false;
  @HostBinding('class.center-content') get isCentered(): boolean {
    return this.centerContent;
  }

  readonly defaultColor: ProgressColorClass = 'primary';
  readonly defaultLayout: ProgressLayout = 'column';

  private readonly commonService = inject(CommonService);
  private readonly commonUtils = inject(CommonUtils);
  private readonly exportService = inject(ExcelExportService);
  private readonly destroyRef = inject(DestroyRef);
  private filterPayload: any = null;

  constructor() {}

  ngOnInit(): void {
    if (this.globalPayloadSubject) {
      this.globalPayloadSubject?.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((payload) => {
        this.filterPayload = payload?.Value?.body ?? null;
        this.loadData();
      });
    }

    if (!this.apiConfig?.path || !this.apiConfig?.urlPath || !this.apiConfig?.method) {
      this.updateInfoFields();
      return;
    }

    this.loadData();
  }

  private loadData(): void {
    if (!this.apiConfig?.path || !this.apiConfig?.urlPath || !this.apiConfig?.method || !this.commonService) {
      return;
    }

    this.bars = [];
    this.isLoading = true;
    const apiConfig = this.buildApiConfig();
    if (!apiConfig.urlPath) {
      return;
    }
    this.commonService.getDataFromDataSource(apiConfig, apiConfig.urlPath).then(response => {
      if (response.status === API_STATUS.Success) {
        this.bars = this.dataTransform ? this.dataTransform(response.result ?? []) : (response.result ?? []);
        this.updateInfoFields();
      }
      this.isLoading = false;
    }).catch(() => {
      this.isLoading = false;
    });
  }

  private buildApiConfig(): SearchConfig {
  if (!this.apiConfig) {
    throw new Error('apiConfig is required');
  }
    const config = structuredClone(this.apiConfig!);
    if (this.filterPayload) {
      config.body = {
        ...(config.body ),
        ...this.filterPayload,
      };
    }
    return config;
  }

  trackByLabel = (_: number, bar: ProgressBarItem) => bar.label;

  barValue(bar: ProgressBarItem): string {
    return bar.countDisplay || `${bar.count ?? 0}`;
  }

  barCaption(bar: ProgressBarItem): string {
    if (bar.caption) {
      return bar.caption;
    }
    return `${bar.pct.toFixed(0)}% of certifiable`;
  }

  barLayout(bar: ProgressBarItem): ProgressLayout {
    return bar.layout || this.defaultLayout;
  }

  updateInfoFields(): void {
    if (!this.infoFields) { return; }
    this.infoFields.forEach(field => {
      if (field.infoType === INFO_TYPES.Total || field.infoType === INFO_TYPES.Count) {
        field.value = this.bars.reduce((sum, item: any) => sum + Number(item[field.calculateBy] || 0), 0);
      }
    });
  }

  downloadActionOnClick(): void {
    if (!this.exportConfig || !this.commonUtils || !this.exportService) { return; }
    const fileName = this.exportConfig.fileName ?? '';
    const detailRows = this.bars.flatMap(bar => bar.responseDetails ?? []);
    if (!detailRows.length) {
      const data = this.commonUtils.formatExcelData(this.exportConfig, this.bars);
      this.exportService.exportAsExcelFile(data, fileName, this.exportConfig.descriptions ?? []);
      return;
    }
    const detailsConfig: exportConfig = this.detailsExportConfig ?? { fileName };
    const summarySheetName = this.exportConfig.exportMessage ?? 'Summary';
    const detailsSheetName = detailsConfig.exportMessage ?? 'Response Details';
    const dataset: Record<string, any[]> = {
      [summarySheetName]: structuredClone(this.commonUtils.formatExcelData(this.exportConfig, this.bars)) ?? [],
      [detailsSheetName]: structuredClone(this.commonUtils.formatExcelData(detailsConfig, detailRows)) ?? [],
    };
    this.exportService.exportToMultipleSheets(dataset, fileName);
  }
}
